@let isManager = isManager$ | async;

<ng-template #patientFormButton>
    <div class="flex flex-row end gap-medpace">
        @if (isUpdate && isAssistantOrHigher()) {
            <mds-button
                [disabled]="patient?.clinTrakStatus ? true : false"
                (clickEvent)="changePatientStatus()"
                [id]="'ChangeStatusRequestBtn'"
                label="Change status"
                color="primary"
                [variant]="'tonal'"
                [matTooltip]="patient?.clinTrakStatus ? 'Status from ClinTrak available' : null"
            >
            </mds-button>
        }
        <mds-button
            (clickEvent)="buttonClick(true)"
            [color]="'primary'"
            [variant]="'filled'"
            [label]="label"
            [id]="'createPatientBtn'"
            type="submit"
        ></mds-button>
    </div>
</ng-template>
<form [formGroup]="patientFormGroup" appScrollToInvalidControlOnSubmit class="o-grid">
    <div class="o-grid__row">
        <div class="o-grid__item third stepper-min-width">
            <medpace-vertical-stepper [steps]="currentSteps"></medpace-vertical-stepper>
        </div>
        <div class="o-grid__item">
            <div class="o-grid">
                <div class="o-grid__row">
                    <div class="o-grid__card">
                        <div class="flex start middle">
                            <span class="required-asterisk">*</span>
                            <span class="note"> indicates a required field </span>
                        </div>
                    </div>
                    <div class="o-grid__card">
                        <ng-container *ngTemplateOutlet="patientFormButton"></ng-container>
                    </div>
                </div>
                <div class="o-grid__row">
                    <div class="o-grid__card" id="studyInformation">
                        <medpace-input-card
                            (inputChangeEvent)="fieldChangeEvent($event)"
                            (submitClickEvent)="buttonClick($event)"
                            [additionalContent]="site"
                            [inputObj]="study"
                            [showEditSaveButton]="false"
                            [editMode]="false"
                            [patient]="patient"
                            [parentFormGroup]="patientFormGroup"
                            title="Study Details"
                            type="study-detail"
                        >
                        </medpace-input-card>
                    </div>
                </div>

                @if (patient) {
                    <div class="o-grid__row">
                        <div class="o-grid__card" id="patientConsent">
                            <medpace-input-card
                                (incrementalUpdate)="submitIncrementalSave()"
                                (inputChangeEvent)="fieldChangeEvent($event)"
                                [showEditSaveButton]="isUpdate"
                                [editMode]="!isUpdate"
                                [isRequired]="true"
                                [parentFormGroup]="patientFormGroup"
                                title="Consent Form"
                                type="consent-card"
                            >
                            </medpace-input-card>
                        </div>
                    </div>
                    <div class="o-grid__row">
                        <div class="o-grid__card" id="caregiverInformation">
                            <medpace-input-card
                                [parentFormGroup]="patientFormGroup"
                                (incrementalUpdate)="submitIncrementalSave()"
                                title="Caregiver Information"
                                type="caregiver-info"
                                [showEditSaveButton]="false"
                                [isPatientProfile]="true"
                                [editMode]="false"
                            >
                            </medpace-input-card>
                        </div>
                    </div>

                    @if (siteServices?.travel) {
                        <div class="o-grid__row">
                            <div class="o-grid__card" id="travelPreferences">
                                <medpace-input-card
                                    (incrementalUpdate)="submitIncrementalSave()"
                                    (inputChangeEvent)="fieldChangeEvent($event)"
                                    [showEditSaveButton]="isUpdate"
                                    [editMode]="!isUpdate"
                                    [isUpdate]="isUpdate"
                                    [isPatientProfile]="true"
                                    [parentFormGroup]="patientFormGroup"
                                    title="Travel Information"
                                    type="travel-card"
                                ></medpace-input-card>
                            </div>
                        </div>
                    }
                }

                @if (site && paymentRequired && (siteServices?.reimbursement || siteServices?.stipend)) {
                    <div class="o-grid__row">
                        <div class="o-grid__card" id="paymentInformation">
                            <medpace-input-card
                                (incrementalUpdate)="submitIncrementalSave()"
                                (inputChangeEvent)="fieldChangeEvent($event)"
                                [showEditSaveButton]="isUpdate"
                                [editMode]="!isUpdate"
                                [isPatientProfile]="true"
                                [elevatedAccess]="isAssistantOrHigher()"
                                [siteSettings]="siteServices"
                                [parentFormGroup]="patientFormGroup"
                                title="Payment Information"
                                type="payment-card"
                            ></medpace-input-card>
                        </div>
                    </div>
                }

                @if (patient) {
                    <div class="o-grid__row" id="additionalInformation">
                        <div class="o-grid__card">
                            <medpace-input-card
                                (incrementalUpdate)="submitIncrementalSave()"
                                (inputChangeEvent)="fieldChangeEvent($event)"
                                [parentFormGroup]="patientFormGroup"
                                title="Additional Information"
                                type="accessiblity-card"
                                [showEditSaveButton]="isUpdate"
                                [editMode]="!isUpdate"
                            ></medpace-input-card>
                        </div>
                    </div>
                }
                @if (isAssistantOrHigher()) {
                    <div class="o-grid__row">
                        <div class="o-grid__card" id="patientNote">
                            @if (isUpdate) {
                                <medpace-note-card
                                    [mode]="'view'"
                                    [title]="'Patient Notes'"
                                    [showSwitchModeButton]="true"
                                    (saveEvent)="savePatientNote()"
                                    [notes]="noteStore.patientNotes()"
                                    [context]="NoteCardContext.Patient"
                                ></medpace-note-card>
                            } @else {
                                <medpace-note-card
                                    [mode]="'edit'"
                                    [title]="'Patient Notes'"
                                    [showSwitchModeButton]="false"
                                    [notes]="noteStore.patientNotes()"
                                    (saveEvent)="savePatientNote()"
                                    [context]="NoteCardContext.Patient"
                                ></medpace-note-card>
                            }
                        </div>
                    </div>
                }

                <div class="o-grid__row text-end pb-4">
                    <!-- There seems to be no final conclusion on how "Delete patient" should work yet. The current preference is to have it hidden
                        but not removed from the code base yet. More on this topic was discussed in:
                         - https://dev.azure.com/Medpace/PCS/_workitems/edit/14077
                         - https://dev.azure.com/Medpace/PCS/_workitems/edit/15587
                    -->
                    @if (isManager && isUpdate && false) {
                        <div class="o-grid__item u-ta-left">
                            <mds-button
                                [id]="'deleteBtn'"
                                (clickEvent)="deletePatient()"
                                variant="outline"
                                color="warn"
                                label="Delete patient"
                                width="auto"
                            ></mds-button>
                        </div>
                    }

                    <ng-container *ngTemplateOutlet="patientFormButton"></ng-container>
                </div>
            </div>
        </div>
    </div>
</form>
